<app-menu>
  <div class="rate-shell" [attr.data-theme]="themeMode">
    <div class="mx-auto flex min-h-[calc(100vh-64px)] w-full max-w-[1760px] flex-col gap-4 px-4 py-4">
      <header class="rate-card flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <div class="min-w-0 space-y-2">
          <div class="rate-eyebrow">Customer Tools / Rates</div>
          <div class="flex min-w-0 flex-wrap items-center gap-3">
            <h1 class="m-0 text-[1.45rem] font-[850] leading-none text-[var(--rate-text)]">Customer Rate Quote</h1>
            <span class="rate-pill" [ngClass]="statusClass(response?.status)">
              <i class="fa-solid" [ngClass]="statusIcon(response?.status)"></i>
              {{ statusTitle(response?.status) }}
            </span>
          </div>
          <div class="flex min-w-0 flex-wrap items-center gap-2">
            <span class="rate-meta-pill"><strong>{{ quoteForm.quote_date || 'Today' }}</strong> quote date</span>
            <span class="rate-meta-pill"><strong>{{ customerScopeLabel }}</strong></span>
            <span class="rate-meta-pill" *ngIf="response?.meta"><strong>{{ response?.meta?.candidate_count || 0 }}</strong> candidates</span>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2 lg:justify-end">
          <button type="button" class="rate-button rate-button--ghost" (click)="toggleTheme()" [title]="getThemeLabel() + ' theme'">
            <i class="fa-solid" [ngClass]="getThemeIcon()"></i>
            <span>{{ getThemeLabel() }}</span>
          </button>
          <button type="button" class="rate-button rate-button--ghost" (click)="resetQuote()" [disabled]="loading">
            <i class="fa-solid fa-arrow-rotate-left"></i>
            <span>Reset</span>
          </button>
          <button type="button" class="rate-button rate-button--primary" (click)="quoteRate()" [disabled]="loading">
            <i class="fa-solid" [ngClass]="loading ? 'fa-circle-notch fa-spin' : 'fa-calculator'"></i>
            <span>{{ loading ? 'Checking' : 'Check Rate' }}</span>
          </button>
        </div>
      </header>

      <section class="rate-card p-4" *ngIf="canSelectCustomer">
        <div class="grid gap-3 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-start">
          <div class="rate-section-title mb-0">
            <i class="fa-solid fa-building-user"></i>
            <div>
              <span class="rate-eyebrow">Admin Scope</span>
              <h2>Customer</h2>
            </div>
          </div>

          <div class="rate-admin-selector min-w-0">
            <app-contact-autocomplete
              placeholder="Search customer..."
              [type]="'customer_rate_contact_list'"
              [formControl]="customerControl"
              [panelTheme]="themeMode"
              (selected)="onCustomerSelected($event)"
            ></app-contact-autocomplete>
          </div>

          <div class="flex min-w-0 items-center gap-2 lg:justify-end">
            <span class="rate-selected-customer" [class.is-empty]="!selectedCustomer">
              <i class="fa-solid" [ngClass]="selectedCustomer ? 'fa-circle-check' : 'fa-circle-info'"></i>
              <span class="rate-selected-customer-text">
                <strong>{{ selectedCustomer?.label || 'No customer selected' }}</strong>
                <small *ngIf="selectedCustomer?.value">{{ selectedCustomer?.value }}</small>
              </span>
            </span>
            <button type="button" class="rate-button rate-button--ghost" (click)="clearSelectedCustomer()" [disabled]="loading || !selectedCustomer">
              <i class="fa-solid fa-xmark"></i>
              <span>Clear</span>
            </button>
          </div>
        </div>
      </section>

      <div class="rate-alert rate-alert--error" *ngIf="errorMessage">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <main class="rate-main-grid flex-1">
        <form class="rate-card overflow-hidden" (ngSubmit)="quoteRate()">
          <div class="space-y-5 p-4">
            <section class="space-y-3">
              <div class="rate-section-title">
                <i class="fa-solid fa-route"></i>
                <h2>Lane</h2>
              </div>

              <div class="rate-saved-origins" *ngIf="showShippingOrigins">
                <div class="rate-saved-origin-head">
                  <span>Saved Origins</span>
                  <button type="button" *ngIf="selectedShippingOrigin" (click)="clearSelectedShippingOrigin()">Manual</button>
                </div>

                <div class="rate-saved-origin-state" *ngIf="shippingTargetsLoading">
                  <i class="fa-solid fa-circle-notch fa-spin"></i>
                  <span>Loading origins</span>
                </div>

                <div class="rate-saved-origin-list" *ngIf="!shippingTargetsLoading && shippingOriginOptions.length">
                  <button
                    type="button"
                    class="rate-saved-origin-option"
                    *ngFor="let option of shippingOriginOptions; trackBy: trackByIndex"
                    [class.is-active]="selectedShippingOriginId === option.id"
                    (click)="selectShippingOrigin(option)"
                  >
                    <i class="fa-solid fa-warehouse"></i>
                    <span>
                      <strong>{{ option.label }}</strong>
                      <small>{{ option.subline }}</small>
                    </span>
                  </button>
                </div>

                <div class="rate-saved-origin-state rate-saved-origin-state--warning" *ngIf="!shippingTargetsLoading && shippingTargetsError">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  <span>{{ shippingTargetsError }}</span>
                </div>

                <div class="rate-saved-origin-state" *ngIf="!shippingTargetsLoading && !shippingTargetsError && shippingTargetsLoaded && !shippingOriginOptions.length">
                  <i class="fa-solid fa-circle-info"></i>
                  <span>No saved origins</span>
                </div>
              </div>

              <div class="rate-lane-pair">
                <div class="rate-subsection">
                  <div class="rate-subsection-head">
                    <span>Origin</span>
                    <i class="fa-solid fa-location-dot"></i>
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <label class="rate-field col-span-full">
                      <span>Postal Code</span>
                      <input class="rate-input" type="text" name="origin_postal_code" autocomplete="postal-code" [(ngModel)]="quoteForm.origin.postal_code" (ngModelChange)="onOriginManualEdit()">
                    </label>
                    <label class="rate-field col-span-full">
                      <span>City</span>
                      <input class="rate-input" type="text" name="origin_city" autocomplete="address-level2" [(ngModel)]="quoteForm.origin.city" (ngModelChange)="onOriginManualEdit()">
                    </label>
                    <label class="rate-field">
                      <span>Province</span>
                      <input class="rate-input" type="text" name="origin_province" autocomplete="address-level1" maxlength="8" [(ngModel)]="quoteForm.origin.province" (ngModelChange)="onOriginManualEdit()">
                    </label>
                    <label class="rate-field">
                      <span>Country</span>
                      <select class="rate-input" name="origin_country" [(ngModel)]="quoteForm.origin.country" (ngModelChange)="onOriginManualEdit()">
                        <option *ngFor="let country of countryOptions" [value]="country.value">{{ country.label }}</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div class="rate-subsection">
                  <div class="rate-subsection-head">
                    <span>Destination</span>
                    <i class="fa-solid fa-flag-checkered"></i>
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <label class="rate-field col-span-full">
                      <span>Postal Code</span>
                      <input class="rate-input" type="text" name="destination_postal_code" autocomplete="postal-code" [(ngModel)]="quoteForm.destination.postal_code">
                    </label>
                    <label class="rate-field col-span-full">
                      <span>City</span>
                      <input class="rate-input" type="text" name="destination_city" autocomplete="address-level2" [(ngModel)]="quoteForm.destination.city">
                    </label>
                    <label class="rate-field">
                      <span>Province</span>
                      <input class="rate-input" type="text" name="destination_province" autocomplete="address-level1" maxlength="8" [(ngModel)]="quoteForm.destination.province">
                    </label>
                    <label class="rate-field">
                      <span>Country</span>
                      <select class="rate-input" name="destination_country" [(ngModel)]="quoteForm.destination.country">
                        <option *ngFor="let country of countryOptions" [value]="country.value">{{ country.label }}</option>
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            </section>

            <section class="space-y-3">
              <div class="rate-section-title">
                <i class="fa-solid fa-boxes-stacked"></i>
                <h2>Freight</h2>
              </div>

              <div class="rate-product-state" *ngIf="hasConfiguredFreightPresentation">
                <i class="fa-solid fa-id-card-clip"></i>
                <span><strong>{{ freightPresentationName }}</strong> — {{ freightPresentationSummary }}</span>
              </div>

              <div class="rate-product-state" *ngIf="productsLoading && !freightPresentation">
                <i class="fa-solid fa-circle-notch fa-spin"></i>
                <span>Loading customer freight profile</span>
              </div>

              <div class="rate-freight-builder" *ngIf="showPackageBuilder && (!canSelectCustomer || selectedCustomer)">
                <div class="rate-builder-head">
                  <div>
                    <strong>Shipping Packages</strong>
                    <small *ngIf="productsLoaded">{{ productTotal }} customer products / {{ totalSelectedPackageCount }} packages selected</small>
                  </div>
                  <span class="rate-meta-pill" *ngIf="productSelectionMode === 'autocomplete'">Type 3+ characters</span>
                </div>

                <ng-container *ngIf="allowCatalogProducts">
                  <label class="rate-field">
                    <span>Find Customer Product</span>
                    <input
                      class="rate-input"
                      type="search"
                      name="product_search"
                      autocomplete="off"
                      placeholder="Model, product name, barcode, or class"
                      [ngModel]="productSearch"
                      (ngModelChange)="onProductSearchChanged($event)"
                    >
                  </label>

                  <div class="rate-product-state" *ngIf="productsLoading">
                    <i class="fa-solid fa-circle-notch fa-spin"></i>
                    <span>Loading products</span>
                  </div>

                  <div class="rate-product-results" *ngIf="!productsLoading && productSearch.trim().length && displayedProductMatches.length">
                    <button type="button" class="rate-product-option" *ngFor="let product of displayedProductMatches; trackBy: trackByIndex" (click)="addCatalogProduct(product)">
                      <i class="fa-solid fa-box"></i>
                      <span>
                        <strong>{{ productDisplayName(product) }}</strong>
                        <small>{{ productSubline(product) }}</small>
                      </span>
                      <i class="fa-solid fa-plus"></i>
                    </button>
                    <div class="rate-product-more" *ngIf="productMoreMatches">More matches exist—keep typing to narrow the list.</div>
                  </div>

                  <div class="rate-product-state rate-product-state--warning" *ngIf="!productsLoading && productsError">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <span>{{ productsError }}</span>
                  </div>
                </ng-container>

                <div class="rate-selected-packages" *ngIf="selectedProducts.length || manualPackages.length">
                  <div class="rate-selected-package" *ngFor="let selected of selectedProducts; let index = index; trackBy: trackByIndex">
                    <div>
                      <strong>{{ productDisplayName(selected.product) }}</strong>
                      <small>{{ productSubline(selected.product) }}</small>
                    </div>
                    <label>
                      <span>Qty</span>
                      <input type="number" min="1" step="1" [name]="'catalog_quantity_' + index" [(ngModel)]="selected.package_quantity">
                    </label>
                    <button type="button" (click)="removeCatalogProduct(index)" title="Remove product"><i class="fa-solid fa-xmark"></i></button>
                  </div>

                  <div class="rate-selected-package" *ngFor="let selected of manualPackages; let index = index; trackBy: trackByIndex">
                    <div>
                      <strong>{{ selected.label }}</strong>
                      <small>{{ selected.length }} × {{ selected.width }} × {{ selected.height }} {{ selected.measurement_unit }} / {{ selected.stackable ? 'Stackable' : 'Not stackable' }} / {{ selected.floor_rotation_allowed ? 'Floor rotation allowed' : 'No floor rotation' }}</small>
                    </div>
                    <label>
                      <span>Qty</span>
                      <input type="number" min="1" step="1" [name]="'manual_quantity_' + selected.id" [(ngModel)]="selected.package_quantity">
                    </label>
                    <button type="button" (click)="removeManualPackage(index)" title="Remove package"><i class="fa-solid fa-xmark"></i></button>
                  </div>
                </div>

                <div class="rate-custom-package" *ngIf="allowManualPackages">
                  <div class="rate-subsection-head">
                    <span>Add Custom Package</span>
                    <i class="fa-solid fa-ruler-combined"></i>
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <label class="rate-field col-span-full">
                      <span>Label <small>optional</small></span>
                      <input class="rate-input" type="text" name="manual_package_label" [(ngModel)]="manualPackageForm.label" placeholder="Custom crate">
                    </label>
                    <label class="rate-field">
                      <span>Quantity</span>
                      <input class="rate-input" type="number" min="1" step="1" name="manual_package_quantity" [(ngModel)]="manualPackageForm.package_quantity">
                    </label>
                    <label class="rate-field">
                      <span>Dimension Unit</span>
                      <select class="rate-input" name="manual_measurement_unit" [(ngModel)]="manualPackageForm.measurement_unit">
                        <option value="IN">Inches</option>
                        <option value="CM">Centimeters</option>
                      </select>
                    </label>
                    <label class="rate-field">
                      <span>Length</span>
                      <input class="rate-input" type="number" min="0" step="0.1" name="manual_package_length" [(ngModel)]="manualPackageForm.length">
                    </label>
                    <label class="rate-field">
                      <span>Width</span>
                      <input class="rate-input" type="number" min="0" step="0.1" name="manual_package_width" [(ngModel)]="manualPackageForm.width">
                    </label>
                    <label class="rate-field">
                      <span>Height</span>
                      <input class="rate-input" type="number" min="0" step="0.1" name="manual_package_height" [(ngModel)]="manualPackageForm.height">
                    </label>
                    <label class="rate-field">
                      <span>Weight <small>optional</small></span>
                      <input class="rate-input" type="number" min="0" step="0.1" name="manual_package_weight" [(ngModel)]="manualPackageForm.weight">
                    </label>
                    <label class="rate-field">
                      <span>Weight Unit</span>
                      <select class="rate-input" name="manual_weight_unit" [(ngModel)]="manualPackageForm.weight_unit">
                        <option value="LB">Pounds</option>
                        <option value="KG">Kilograms</option>
                      </select>
                    </label>
                    <label class="rate-check-field">
                      <input type="checkbox" name="manual_package_stackable" [(ngModel)]="manualPackageForm.stackable">
                      <span>Stackable <small>maximum 3 high when height allows</small></span>
                    </label>
                    <label class="rate-check-field">
                      <input type="checkbox" name="manual_package_floor_rotation_allowed" [(ngModel)]="manualPackageForm.floor_rotation_allowed">
                      <span>Allow 90° floor rotation <small>length and width only; never tips the package</small></span>
                    </label>
                  </div>
                  <button type="button" class="rate-button rate-button--ghost w-full" (click)="addManualPackage()">
                    <i class="fa-solid fa-plus"></i>
                    <span>Add Custom Package</span>
                  </button>
                </div>
              </div>

              <div class="rate-product-state" *ngIf="showPackageBuilder && canSelectCustomer && !selectedCustomer">
                <i class="fa-solid fa-circle-info"></i>
                <span>Select a customer to load its freight profile and product catalog.</span>
              </div>

              <ng-container *ngIf="showManualFreight">
                <div class="rate-builder-divider"><span>{{ hasConfiguredFreightPresentation ? 'Profile freight inputs' : 'Manual freight overrides / legacy quote' }}</span></div>

                <div class="rate-product-state" *ngIf="freightPresentation?.mode === 'catalog_equipment_linear_feet' && isFreightFieldVisible('linear_feet')">
                  <i class="fa-solid fa-ruler-horizontal"></i>
                  <span>{{ usesCalculatedLinearFeet ? 'Linear feet will be calculated from your selected products and packages.' : 'Enter the occupied linear feet to quote without selecting products or packages.' }}</span>
                </div>

                <div class="grid grid-cols-2 gap-2" *ngIf="showRateMethodPicker">
                  <button type="button" class="rate-method-option" *ngFor="let method of rateMethodOptions" [class.is-active]="quoteForm.freight.preferred_rate_method === method.value" (click)="setPreferredRateMethod(method.value)">
                    <i class="fa-solid" [ngClass]="method.icon"></i>
                    <span>{{ method.label }}</span>
                  </button>
                </div>

                <div class="grid grid-cols-2 gap-3">
                <label class="rate-field" *ngIf="isFreightFieldVisible('total_weight_lb')">
                  <span>Total Weight <small>lb</small></span>
                  <input class="rate-input" type="number" name="total_weight_lb" min="0" step="1" inputmode="decimal" [(ngModel)]="quoteForm.freight.total_weight_lb">
                </label>
                <label class="rate-field rate-field--featured" *ngIf="isFreightFieldVisible('linear_feet')">
                  <span>Linear Feet</span>
                  <input class="rate-input" type="number" name="linear_feet" min="0" step="0.1" inputmode="decimal" [disabled]="usesCalculatedLinearFeet" [(ngModel)]="quoteForm.freight.linear_feet">
                </label>
                <label class="rate-field" *ngIf="isFreightFieldVisible('handling_units')">
                  <span>Handling Units</span>
                  <input class="rate-input" type="number" name="handling_units" min="0" step="1" inputmode="numeric" [(ngModel)]="quoteForm.freight.handling_units">
                </label>
                <label class="rate-field" *ngIf="isFreightFieldVisible('cubic_feet')">
                  <span>Cubic Feet</span>
                  <input class="rate-input" type="number" name="cubic_feet" min="0" step="0.1" inputmode="decimal" [(ngModel)]="quoteForm.freight.cubic_feet">
                </label>
                <label class="rate-field" *ngIf="isFreightFieldVisible('density_lb_per_cuft')">
                  <span>Density <small>lb/cuft</small></span>
                  <input class="rate-input" type="number" name="density_lb_per_cuft" min="0" step="0.01" inputmode="decimal" [(ngModel)]="quoteForm.freight.density_lb_per_cuft">
                </label>
                <label class="rate-field" *ngIf="isFreightFieldVisible('delivery_type')">
                  <span>Delivery Type</span>
                  <input class="rate-input" type="number" name="delivery_type" step="1" inputmode="numeric" [(ngModel)]="quoteForm.freight.delivery_type">
                </label>
                <label class="rate-field col-span-full sm:col-span-1 xl:col-span-full 2xl:col-span-1" *ngIf="isFreightFieldVisible('load_type')">
                  <span>Load Type</span>
                  <input class="rate-input" type="text" name="load_type" [(ngModel)]="quoteForm.freight.load_type">
                </label>
              </div>
              </ng-container>

              <label class="rate-field">
                <span>External Reference Number (Optional)</span>
                <input class="rate-input" type="text" name="external_reference_number" autocomplete="off" maxlength="200" placeholder="Your PO, order, or reference number" [(ngModel)]="quoteForm.external_reference_number" [disabled]="quoteCreating || !!createdQuote">
              </label>

              <label class="rate-field">
                <span>Quote Date</span>
                <input class="rate-input" type="date" name="quote_date" [(ngModel)]="quoteForm.quote_date">
              </label>
            </section>
          </div>

          <div class="flex justify-end border-t border-[var(--rate-border)] px-4 py-3">
            <button type="submit" class="rate-button rate-button--primary" [disabled]="loading">
              <i class="fa-solid" [ngClass]="loading ? 'fa-circle-notch fa-spin' : 'fa-calculator'"></i>
              <span>{{ loading ? 'Checking' : 'Check Rate' }}</span>
            </button>
          </div>
        </form>

        <section class="rate-card min-h-[34rem] overflow-hidden">
          <ng-container *ngIf="response as quoteResponse; else emptyQuote">
            <div class="flex items-start gap-3 border-b border-[var(--rate-border)] px-5 py-4" [ngClass]="statusClass(quoteResponse.status)">
              <i class="fa-solid mt-1" [ngClass]="statusIcon(quoteResponse.status)"></i>
              <div class="min-w-0">
                <strong class="block text-[1rem] font-[900] text-[var(--rate-text)]">{{ statusTitle(quoteResponse.status) }}</strong>
                <span class="mt-1 block text-sm font-semibold text-[var(--rate-muted)]">{{ statusMessage(quoteResponse.status) }}</span>
              </div>
            </div>

            <ng-container [ngSwitch]="quoteResponse.status">
              <section *ngSwitchCase="'rated'" class="space-y-4 p-5">
                <div class="rate-total-band">
                  <span>Total</span>
                  <strong>{{ formatMoney(quoteTotal, quoteCurrency) }}</strong>
                </div>

                <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <span class="rate-fact"><small>Rate Method</small><strong>{{ selectedRateMethod }}</strong></span>
                  <span class="rate-fact"><small>Linear Feet Used</small><strong>{{ linearFeetUsed }}</strong></span>
                  <span class="rate-fact"><small>Origin Zone</small><strong>{{ originZoneName }}</strong></span>
                  <span class="rate-fact"><small>Destination Zone</small><strong>{{ destinationZoneName }}</strong></span>
                  <span class="rate-fact"><small>Transit Time</small><strong>{{ transitTime }}</strong></span>
                  <span class="rate-fact"><small>Equipment</small><strong>{{ selectedEquipmentName }}</strong></span>
                  <span class="rate-fact" *ngIf="quoteResponse.meta.rating_basis === 'mileage'"><small>Billable Miles</small><strong>{{ billableMiles }}</strong></span>
                </div>

                <div class="space-y-2" *ngIf="lineItems.length">
                  <div class="rate-line-row" *ngFor="let item of lineItems; trackBy: trackByIndex">
                    <span>{{ lineItemLabel(item) }}</span>
                    <strong>{{ lineItemAmount(item) }}</strong>
                  </div>
                </div>

                <div class="space-y-2" *ngIf="warnings.length || quoteNotes.length">
                  <div class="rate-notice rate-notice--warning" *ngFor="let warning of warnings; trackBy: trackByIndex">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <span>{{ warning }}</span>
                  </div>
                  <div class="rate-notice rate-notice--info" *ngFor="let note of quoteNotes; trackBy: trackByIndex">
                    <i class="fa-solid fa-circle-info"></i>
                    <span>{{ note }}</span>
                  </div>
                </div>

                <div class="rounded-lg border border-[var(--rate-border)] bg-[var(--rate-panel-2)] p-4" *ngIf="quoteConditions.length">
                  <div class="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--rate-border)] pb-3">
                    <div>
                      <span class="rate-eyebrow">Customer Quote</span>
                      <strong class="mt-1 block text-sm font-black text-[var(--rate-text)]">Conditions of Service</strong>
                    </div>
                    <span class="rate-meta-pill" *ngIf="quoteResponse.quote.expiry_date">
                      Valid {{ quoteResponse.quote.effective_date }} through <strong>{{ quoteResponse.quote.expiry_date }}</strong>
                    </span>
                  </div>

                  <div class="mt-3 grid grid-cols-1 gap-2 xl:grid-cols-2">
                    <div class="rounded-md border border-[var(--rate-border)] bg-[var(--rate-input)] px-3 py-2" *ngFor="let condition of quoteConditions; trackBy: trackByIndex">
                      <strong class="block text-xs font-black text-[var(--rate-text)]">{{ condition.title }}</strong>
                      <span class="mt-1 block text-xs font-semibold leading-5 text-[var(--rate-muted)]">{{ condition.text }}</span>
                    </div>
                  </div>

                  <div class="rate-notice rate-notice--warning mt-3" *ngIf="!quoteInputsUnchanged && !createdQuote">
                    <i class="fa-solid fa-rotate"></i>
                    <span>The quote details changed after this result. Check the rate again before creating a quote.</span>
                  </div>

                  <label class="mt-3 flex cursor-pointer items-start gap-3 rounded-md border border-[var(--rate-border)] bg-[var(--rate-input)] p-3" *ngIf="!createdQuote">
                    <input class="mt-1 h-4 w-4 accent-[var(--rate-primary)]" type="checkbox" [(ngModel)]="quoteConditionsAccepted" [disabled]="quoteCreating || !quoteInputsUnchanged">
                    <span class="min-w-0">
                      <strong class="block text-sm font-black text-[var(--rate-text)]">I accept these quote conditions</strong>
                      <small class="mt-1 block text-xs font-semibold leading-5 text-[var(--rate-muted)]">Creating the quote saves this freight request, the final price, your external reference, these conditions, and a downloadable PDF.</small>
                    </span>
                  </label>

                  <div class="rate-notice rate-notice--warning mt-3" *ngIf="quoteCreateError">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <span>{{ quoteCreateError }}</span>
                  </div>

                  <div class="mt-3 flex flex-wrap items-center justify-between gap-3" *ngIf="!createdQuote; else quoteCreated">
                    <span class="text-xs font-semibold text-[var(--rate-muted)]">The server rerates once more before assigning the quote number.</span>
                    <button type="button" class="rate-button rate-button--primary" (click)="createQuoteFromRate()" [disabled]="!canCreateCurrentQuote">
                      <i class="fa-solid" [ngClass]="quoteCreating ? 'fa-circle-notch fa-spin' : 'fa-file-circle-check'"></i>
                      <span>{{ quoteCreating ? 'Creating Quote' : 'Create Quote' }}</span>
                    </button>
                  </div>

                  <ng-template #quoteCreated>
                    <div class="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--rate-success)] bg-[var(--rate-success-soft)] px-3 py-3 text-[var(--rate-success)]">
                      <span class="flex items-center gap-2 text-sm font-black">
                        <i class="fa-solid fa-circle-check"></i>
                        {{ createdQuote?.display_quote_number }} created
                      </span>
                      <span class="text-xs font-bold">Status: {{ createdQuote?.effective_status }}</span>
                      <button type="button" class="rate-button rate-button--primary" (click)="downloadQuotePdf()" [disabled]="quotePdfDownloading">
                        <i class="fa-solid" [ngClass]="quotePdfDownloading ? 'fa-circle-notch fa-spin' : 'fa-file-arrow-down'"></i>
                        <span>{{ quotePdfDownloading ? 'Downloading' : 'Download PDF' }}</span>
                      </button>
                    </div>
                    <div class="rate-notice rate-notice--warning mt-3" *ngIf="quotePdfError">
                      <i class="fa-solid fa-triangle-exclamation"></i>
                      <span>{{ quotePdfError }}</span>
                    </div>
                  </ng-template>
                </div>
              </section>

              <section *ngSwitchCase="'needs_zone_selection'" class="space-y-4 p-5">
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div class="rate-zone-picker">
                    <div class="rate-zone-head">
                      <h3>Origin Zone</h3>
                      <span>{{ originZoneMatches.length }} match{{ originZoneMatches.length === 1 ? '' : 'es' }}</span>
                    </div>
                    <button type="button" class="rate-zone-option" *ngFor="let match of originZoneMatches; trackBy: trackByZoneUuid" [class.is-selected]="selectedOriginZoneUuid === match.zone_uuid" (click)="selectedOriginZoneUuid = match.zone_uuid">
                      <i class="fa-solid fa-map-pin"></i>
                      <span>
                        <strong>{{ zoneMatchLabel(match) }}</strong>
                        <small>{{ zoneMatchMeta(match) }}</small>
                      </span>
                    </button>
                    <div class="rate-zone-resolved" *ngIf="!originZoneMatches.length && quoteResponse.lane.origin_zone?.zone_uuid">
                      {{ quoteResponse.lane.origin_zone?.zone_name || quoteResponse.lane.origin_zone?.zone_uuid }}
                    </div>
                  </div>

                  <div class="rate-zone-picker">
                    <div class="rate-zone-head">
                      <h3>Destination Zone</h3>
                      <span>{{ destinationZoneMatches.length }} match{{ destinationZoneMatches.length === 1 ? '' : 'es' }}</span>
                    </div>
                    <button type="button" class="rate-zone-option" *ngFor="let match of destinationZoneMatches; trackBy: trackByZoneUuid" [class.is-selected]="selectedDestinationZoneUuid === match.zone_uuid" (click)="selectedDestinationZoneUuid = match.zone_uuid">
                      <i class="fa-solid fa-map-pin"></i>
                      <span>
                        <strong>{{ zoneMatchLabel(match) }}</strong>
                        <small>{{ zoneMatchMeta(match) }}</small>
                      </span>
                    </button>
                    <div class="rate-zone-resolved" *ngIf="!destinationZoneMatches.length && quoteResponse.lane.destination_zone?.zone_uuid">
                      {{ quoteResponse.lane.destination_zone?.zone_name || quoteResponse.lane.destination_zone?.zone_uuid }}
                    </div>
                  </div>
                </div>

                <button type="button" class="rate-button rate-button--primary" (click)="retryWithZoneSelection()" [disabled]="loading || !canRetryWithZones">
                  <i class="fa-solid" [ngClass]="loading ? 'fa-circle-notch fa-spin' : 'fa-check'"></i>
                  <span>{{ loading ? 'Checking' : 'Use Selected Zones' }}</span>
                </button>
              </section>

              <section *ngSwitchCase="'needs_location_confirmation'" class="p-5">
                <div class="space-y-2" *ngIf="locationCandidates.length">
                  <div class="rate-candidate-row" *ngFor="let item of locationCandidates; trackBy: trackByIndex">
                    <span>{{ item.side }}</span>
                    <strong>{{ candidateLabel(item.candidate) }}</strong>
                    <small>{{ candidateMeta(item.candidate) }}</small>
                  </div>
                </div>
              </section>

              <section *ngSwitchDefault class="p-5">
                <div class="rate-state-panel" [ngClass]="statusClass(quoteResponse.status)">
                  <i class="fa-solid" [ngClass]="statusIcon(quoteResponse.status)"></i>
                  <strong>{{ statusTitle(quoteResponse.status) }}</strong>
                  <span>{{ statusMessage(quoteResponse.status) }}</span>
                </div>
              </section>
            </ng-container>

            <section class="rate-route-preview" *ngIf="routePreviewLoading || routePreview || routePreviewError">
              <div class="rate-route-head">
                <div>
                  <span>Drive Route</span>
                  <strong *ngIf="routePreview as route">{{ route.origin_display }} to {{ route.destination_display }}</strong>
                  <strong *ngIf="routePreviewLoading">Loading route</strong>
                  <strong *ngIf="!routePreviewLoading && routePreviewError">Route unavailable</strong>
                </div>
                <i class="fa-solid" [ngClass]="routePreviewLoading ? 'fa-circle-notch fa-spin' : 'fa-route'"></i>
              </div>

              <div class="rate-route-body" *ngIf="routePreview as route">
                <div class="rate-route-map" *ngIf="routeMapUrl">
                  <img [src]="routeMapUrl" alt="Route map">
                </div>

                <div class="rate-route-facts">
                  <span class="rate-fact"><small>Drive Time</small><strong>{{ route.duration_text }}</strong></span>
                  <span class="rate-fact"><small>No-Traffic Time</small><strong>{{ route.static_duration_text }}</strong></span>
                  <span class="rate-fact"><small>Distance</small><strong>{{ route.distance_text }}</strong></span>
                </div>

                <div class="rate-notice rate-notice--warning" *ngFor="let warning of route.warnings || []; trackBy: trackByIndex">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  <span>{{ warning }}</span>
                </div>
              </div>

              <div class="rate-notice rate-notice--warning" *ngIf="!routePreviewLoading && routePreviewError">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span>{{ routePreviewError }}</span>
              </div>
            </section>
          </ng-container>

          <ng-template #emptyQuote>
            <div class="rate-empty">
              <i class="fa-solid fa-calculator"></i>
              <strong>No Quote Yet</strong>
            </div>
          </ng-template>
        </section>
      </main>
    </div>
  </div>
</app-menu>
